import { isRecord, value } from '@takeshape/util';
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
import { getInitialData } from "../util/initial-data.js";
const title = 'Unstructured';
const defaultStrategy = 'hi_res';
const defaultChunkingStrategy = 'basic';
const paritioningSchema = {
    type: 'object',
    properties: {
        match: {
            title: 'Match',
            type: 'string'
        },
        strategy: {
            title: 'Strategy',
            enum: ['fast', 'hi_res', 'auto', 'ocr_only']
        },
        chunkingStrategy: {
            title: 'Chunking Strategy',
            enum: ['basic', 'by_page', 'by_similarity', 'by_title']
        },
        advanced: {
            title: 'Advanced',
            type: 'string'
        }
    },
    required: ['match', 'strategy'],
    additionalProperties: false
};
const formSchema = {
    type: 'object',
    required: ['title', 'id', 'authentication'],
    properties: {
        title: {
            title: 'Name',
            type: 'string',
            default: 'Unstructured'
        },
        id: {
            title: 'Slug',
            type: 'string',
            pattern: '[0-9a-z_-]+',
            default: 'unstructured'
        },
        namespace: {
            title: 'Namespace',
            type: 'string',
            pattern: '^[A-Z][A-Za-z]+$',
            default: 'Unstructured'
        },
        authentication: {
            title: 'Authentication',
            description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
            type: 'object',
            properties: {
                token: {
                    title: 'API Key',
                    type: 'string'
                }
            },
            required: ['token']
        },
        partitioning: {
            title: 'Document Partitioning',
            type: 'array',
            items: paritioningSchema
        }
    },
    additionalProperties: false
};
export function parseAdvanced(str) {
    if (str) {
        try {
            const parsed = JSON.parse(str);
            if (isRecord(parsed)) {
                return parsed;
            }
        }
        catch {
            // ignore;
        }
    }
    return {};
}
const serviceProvider = {
    name: 'unstructured',
    title,
    description: 'Unstructured helps you get your data ready for AI by transforming it into a format that large language models can understand. Easily connect your data to LLMs.',
    categories: ['ai', 'data-processing'],
    links: [
        { title: 'Homepage', url: 'https://unstructured.io/' },
        { title: 'Docs', url: '/docs/services/providers/unstructured' }
    ],
    serviceType: 'rest',
    createId: value('unstructured'),
    getFormSchema: value(formSchema),
    getFormConfig: value({
        order: ['title', 'id', 'authentication', 'partitioning'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'Get your [API keys here](https://app.unstructured.io/keys)'
                    }
                },
                order: ['token']
            },
            partitioning: {
                items: {
                    title: 'Settings',
                    order: ['match', 'settings', 'strategy', 'chunkingStrategy', 'advanced'],
                    properties: {
                        match: {
                            widget: 'singleLineText',
                            instructions: 'A glob pattern to match files that should be partitioned for example: *.pdf'
                        },
                        strategy: {
                            widget: 'dropdown',
                            instructions: 'The strategy to use for partitioning PDF/image. Options are fast, hi_res, auto.'
                        },
                        chunkingStrategy: {
                            widget: 'dropdown',
                            instructions: "Use one of the supported strategies to chunk the returned elements after partitioning. When 'chunkingStrategy' is not specified, no chunking is performed and any other chunking parameters provided are ignored."
                        },
                        advanced: {
                            widget: 'monaco',
                            isCollapsible: true,
                            instructions: 'Advanced settings see [JavaScript parameters](https://docs.unstructured.io/api-reference/api-services/api-parameters#parameters) for options'
                        }
                    }
                }
            }
        }
    }),
    getInitialData(serviceConfig) {
        const initialData = getInitialData(serviceProvider, {
            ...serviceConfig,
            options: {}
        });
        const partitioning = serviceConfig?.options?.partitioning;
        if (Array.isArray(partitioning)) {
            initialData.partitioning = partitioning.map((profile) => {
                const { match, settings } = profile;
                if (settings) {
                    const { strategy, chunkingStrategy, ...advanced } = settings;
                    return {
                        match,
                        strategy,
                        chunkingStrategy,
                        advanced: JSON.stringify(advanced, null, 2)
                    };
                }
                return {
                    match,
                    strategy: defaultStrategy,
                    chunkingStrategy: defaultChunkingStrategy
                };
            });
        }
        else {
            // Handle PDFs by default
            initialData.partitioning = [
                {
                    match: '*.pdf',
                    strategy: defaultStrategy,
                    chunkingStrategy: defaultChunkingStrategy
                }
            ];
        }
        return initialData;
    },
    getConfig(data) {
        const formData = data;
        const config = {
            title: formData.title,
            provider: 'unstructured',
            serviceType: 'rest',
            authenticationType: 'bearer',
            options: {
                endpoint: 'https://api.unstructuredapp.io'
            }
        };
        if (Array.isArray(formData.partitioning) && config.options) {
            config.options.partitioning = formData.partitioning.map((profile) => {
                const { match, strategy, chunkingStrategy, advanced } = profile;
                return {
                    match,
                    settings: {
                        ...parseAdvanced(advanced),
                        strategy,
                        chunkingStrategy
                    }
                };
            });
        }
        const updated = updateAuthentication(config, formData, {
            type: 'bearer',
            header: 'unstructured-api-key'
        });
        return updated;
    },
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/general/v0/general'),
        requestMethod: 'POST',
        requestBodyFormat: 'form-data',
        requestBody: '{"files":"data:text/plain;base64,SGVsbG8gV29ybGQh","strategy":"fast"}',
        triggers: ['manual']
    }),
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint'])
};
export default serviceProvider;
